import React, {useCallback, useEffect, useState} from 'react';
import {type EnrichedAstNode, type AstView} from 'rendition-core';
import {EditorSectionTitle} from 'Components/Editing/EditingTitle';
import {NumericTextField} from 'Components/Rendition/NumericTextField';
import TabbedButtonToggle from 'Components/Tailwind/TabbedButtonToggle';
import {OrMixed, useViewContext} from 'Contexts/ViewContext';
import {BoundingBox, Square, X} from '@phosphor-icons/react';
import _ from 'lodash';

interface Props {}

export const MarginControl: React.FC<Props> = () => <StyleControl type="margin" />;

export const PaddingControl: React.FC<Props> = () => <StyleControl type="padding" />;

const options: Array<{value: string; icon: React.ReactNode}> = [
    {value: 'all', icon: <Square size={16} color="#ffffff" />},
    {value: 'individual', icon: <BoundingBox size={16} color="#ffffff" />},
];

type DirectionType = 'top' | 'bottom' | 'left' | 'right';

export function adjustPaddings(view: AstView, nodes: EnrichedAstNode[], direction: DirectionType, value: number) {
    const paddingMap = {
        top: 'paddingTop',
        bottom: 'paddingBottom',
        left: 'paddingLeft',
        right: 'paddingRight',
    };

    nodes.forEach((node) => {
        if (!node.inputLayoutAbstraction.userPaddingSet) {
            node.inputLayoutAbstraction.userPaddingSet = {
                paddingLeft: false,
                paddingRight: false,
                paddingTop: false,
                paddingBottom: false,
            };
        }
        node.inputLayoutAbstraction.userPaddingSet[paddingMap[direction]] = true;
        node.innerPosition[paddingMap[direction]] = value;
    });

    return view;
}

const textStyle = 'text-[10px] text-neutral-400';

export function adjustMargins(view: AstView, nodes: EnrichedAstNode[], direction: DirectionType, value: number) {
    const marginMap = {
        top: 'marginTop',
        bottom: 'marginBottom',
        left: 'marginLeft',
        right: 'marginRight',
    };
    nodes.forEach((node) => {
        if (!node.inputLayoutAbstraction.userMarginSet) {
            node.inputLayoutAbstraction.userMarginSet = {
                marginLeft: false,
                marginRight: false,
                marginTop: false,
                marginBottom: false,
            };
        }
        node.inputLayoutAbstraction.userMarginSet[marginMap[direction]] = true;
        node.position[marginMap[direction]] = value;
    });

    return view;
}

const StyleControl: React.FC<{type: 'margin' | 'padding'}> = ({type}) => {
    const {selectedNode, setView, selectedNodes, view} = useViewContext();

    const update = useCallback(
        (direction: DirectionType, value: number) => {
            const adjustedView =
                type === 'margin'
                    ? adjustMargins(view, selectedNodes, direction, value)
                    : adjustPaddings(view, selectedNodes, direction, value);
            setView({...adjustedView});
        },
        [view, selectedNodes, setView, type]
    );

    // Margin uses position, padding uses innerPosition.
    // This method acts as a helper, so we don't have to write the same code twice.
    const getNodePosition = (node: OrMixed<EnrichedAstNode> | null): Record<string, number | 'Mixed'> => {
        if (node === null) {
            return {};
        }
        return type === 'margin' ? node.position : node.innerPosition;
    };

    const allValuesEqual = [`${type}Top`, `${type}Bottom`, `${type}Left`, `${type}Right`].every((key) => {
        if (!selectedNode) {
            return false;
        }
        if (getNodePosition(selectedNode)[key] === 'Mixed') {
            return false;
        }
        return getNodePosition(selectedNode)[key] === getNodePosition(selectedNode)[`${type}Top`];
    });

    const [uniformProps, setUniformProps] = useState({value: allValuesEqual, nodes: selectedNodes});

    // Update uniform props when node changes.
    // Update when selected nodes changes. Keep the value afterwards.
    useEffect(() => {
        if (
            _.isEqual(
                selectedNodes.map((d) => d.internalId),
                uniformProps.nodes.map((d) => d.internalId)
            ) !== true
        ) {
            setUniformProps({value: allValuesEqual, nodes: selectedNodes});
        }
    }, [allValuesEqual, selectedNodes, uniformProps.nodes]);

    const directionsArray: DirectionType[] = ['top', 'bottom', 'left', 'right'];

    return (
        <div className="flex flex-col gap-2 text-white">
            <div className="flex w-full items-center justify-between gap-2">
                <EditorSectionTitle title={type} />
                <div className="flex items-center justify-between gap-2">
                    <div className={textStyle}>All</div>
                    <div className="w-16 ">
                        <NumericTextField
                            dropdown={false}
                            unit={'px'}
                            onValueChange={(value) => {
                                update('top', value);
                                update('left', value);
                                update('bottom', value);
                                update('right', value);
                            }}
                            value={allValuesEqual ? getNodePosition(selectedNode)[`${type}Top`] : 'Mixed'}
                            inputType={'rounded'}
                        />
                    </div>
                    <TabbedButtonToggle
                        classNames="self-stretch h-8"
                        value={uniformProps.value ? 'all' : 'individual'}
                        items={options}
                        onClick={(value) => {
                            setUniformProps({value: value === 'all', nodes: uniformProps.nodes});
                        }}
                    />
                </div>
            </div>

            {!uniformProps.value && selectedNode && (
                <div className="flex flex-row gap-3">
                    {directionsArray.map((direction) => (
                        <ControlComponent
                            key={direction}
                            direction={direction}
                            value={getNodePosition(selectedNode)[`${type}${capitalizeFirstChar(direction)}`]}
                            allValuesEqual={
                                getNodePosition(selectedNode)[`${type}${capitalizeFirstChar(direction)}`] !== 'Mixed'
                            }
                            property={`${type}${capitalizeFirstChar(direction)}`}
                            update={update}
                            selectedNodes={selectedNodes}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

function capitalizeFirstChar(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

type ControlProp = {
    selectedNodes: EnrichedAstNode[];
    direction: DirectionType;
    value: number | 'Mixed' | undefined;
    allValuesEqual: boolean;
    property: string;
    update: (direction: DirectionType, value: number) => void;
};

const ControlComponent: React.FC<ControlProp> = ({
    selectedNodes,
    direction,
    value,
    allValuesEqual,
    property,
    update,
}) => {
    const [showIcon, setShowIcon] = useState(false);

    const handleMouseEnter = () => setShowIcon(true);
    const handleMouseLeave = () => setShowIcon(false);

    const handleIconClick = () => {
        // Set value to zero.
        update(direction, 0);
        // Set animation.
        setClicked(true);
        setTimeout(() => setClicked(false), 250);
    };

    const [clicked, setClicked] = useState(false);

    return (
        <div
            className="items-left flex flex-col justify-center text-sm text-white"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="flex items-center justify-between">
                <div className={textStyle}>{direction}</div>
            </div>
            <NumericTextField
                label=""
                dropdown={false}
                unit={allValuesEqual ? selectedNodes[0].calculatedLayoutAbstraction.units[property] || 'px' : 'px'}
                onValueChange={(value) => update(direction, value)}
                value={value}
                inputType={'rounded'}
            />
            <button
                disabled={value === 0}
                className={`mt-1 flex h-4 w-full items-center
                    justify-center
                    rounded
                    transition-opacity
                    duration-300
                    ${showIcon && value !== 0 ? 'opacity-100' : 'pointer-events-none opacity-0'}
                    ${clicked ? 'bg-white bg-opacity-5' : 'bg-transparent'}
                    ease-in-out
                    hover:scale-110
                    focus:outline-none
                    focus:ring-0`}
                onClick={handleIconClick}
                tabIndex={-1}
            >
                <X size={12} className="transition duration-200 ease-in-out" color="#ffffff" />
            </button>
        </div>
    );
};
